/* global femcare, axios */

femcare.ctaPanel.quickBuy = {
    $quickBuyModal: $('.modal--quick-buy'),
    latestRequestId: 0,
    loadingProductId: null,
    activeProductId: null,
    activeProductRequestData: null,

    /** Initializes on-demand loading inside the generic modal. */
    init: function () {
        if (this.$quickBuyModal.length) {
            this.bindEvents();
        }
    },

    /**
     * Fetches product content and manages loading and retry states.
     * @param {string} productId - Product ID.
     * @param {Object} productRequestData - EAN, URL and selected variations.
     */
    loadProduct: async function (productId, productRequestData = {}) {
        if (this.loadingProductId === productId) {
            return;
        }

        const $modalBody = this.$quickBuyModal.find('.modal__body');
        const $productContent = $modalBody.find('.quick-buy__content');
        const $errorMessage = $modalBody.find('.quick-buy__error');
        this.clearProduct();
        const currentRequestId = ++this.latestRequestId;
        const isLocalDevelopment = femcare.shared.util.isLocalDevelopment();
        const quickBuyApiUrl = femcare.shared.util.resolveProperApi(
            $productContent.attr('data-quick-buy-url'),
            '/json/mock-api/quick-buy-postpartum-kit.json'
        );

        this.loadingProductId = productId;
        this.activeProductId = productId;
        this.activeProductRequestData = productRequestData;
        $errorMessage.addClass('hidden');
        $productContent.attr('aria-busy', 'true');
        femcare.loader.toggleLoader($modalBody[0], true);

        try {
            const requestHeaders = {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json',
            };

            if (!isLocalDevelopment) {
                if (!productRequestData.ean || !productRequestData.url) {
                    throw new Error('Missing product request data');
                }
                const antiforgeryToken = await femcare.form.protection.provideCSRFToken();
                requestHeaders['X-Request-Verification-Token'] = antiforgeryToken;
            }

            if (currentRequestId !== this.latestRequestId) {
                return;
            }

            const requestConfig = {
                headers: requestHeaders,
                timeout: 15000,
            };
            const response = isLocalDevelopment
                ? await axios.get(quickBuyApiUrl, requestConfig)
                : await axios.post(quickBuyApiUrl, {
                    ean: productRequestData.ean,
                    url: productRequestData.url,
                    variationAttributes: productRequestData.variationAttributes || []
                }, requestConfig);

            if (currentRequestId !== this.latestRequestId || !this.$quickBuyModal.hasClass('displayed')) {
                return;
            }

            if (response.data?.success !== true || response.data.validationPassed === false) {
                throw new Error('Product request failed');
            }
            const responseContent = typeof response.data.content === 'string'
                ? JSON.parse(response.data.content) : response.data.content;
            this.initProduct(responseContent?.product, productId, responseContent?.enrichment || {});
        } catch (error) {
            if (currentRequestId === this.latestRequestId) {
                console.error('Quick Buy could not be loaded:', error);
                this.clearProduct();
                $errorMessage.removeClass('hidden');
            }
        } finally {
            if (currentRequestId === this.latestRequestId) {
                this.loadingProductId = null;
                $productContent.attr('aria-busy', 'false');
                femcare.loader.toggleLoader($modalBody[0], false);
            }
        }
    },

    /**
     * Fills the fixed product template from the JSON response.
     * @param {Object} productData - Product fields and options.
     * @param {string} reviewProductId - Card ID used by PowerReviews.
     * @param {Object} enrichment - Product link, shipping and metrics.
     */
    initProduct: function (productData, reviewProductId, enrichment = {}) {
        if (!productData || !productData.productSkuId || !productData.ean || !productData.url ||
            !productData.priceInformation || !productData.productQuantity || !Array.isArray(productData.images) ||
            (productData.optionGroups != null && !Array.isArray(productData.optionGroups))) {
            throw new Error('Invalid Quick Buy product');
        }

        const $productPanel = this.$quickBuyModal.find('.product-main');
        const productShop = femcare.product.panel.info.shop;
        const productRequestUrl = this.activeProductRequestData?.url || enrichment.detailUrl || productData.url;

        $productPanel.attr({
            'data-productid': productData.productId,
            'data-ean': productData.ean,
            'data-product-url': productRequestUrl,
            'data-initial-sku': productData.productSkuId
        }).data('quickBuyRequestId', this.latestRequestId);
        $productPanel.find('.product-main__category').text(productData.name);
        $productPanel.find('.product-main__name').text(productData.productSkuName);
        $productPanel.find('.product-main-purchase__statement').text(enrichment.shipping?.label || '');
        $productPanel.find('.product-main-purchase__tooltip').text(enrichment.shipping?.description || '');
        $productPanel.find('.cta-panel__quick-buy-modal--view-details a').attr('href', enrichment.detailUrl || '');
        $productPanel.find('.counter').attr('data-max', productData.productQuantity.max);

        this.fillOptions(productData.optionGroups || []);
        this.fillMetrics(enrichment.metrics || []);
        productShop.updateSkuPrice(productData.priceInformation, $productPanel);
        productShop.replaceSkuSlides(productData, $productPanel);
        $productPanel.removeClass('hidden');
        productShop.resetSelectedOptions();
        productShop.initProductPanel($productPanel, true);
        $productPanel.data('productSku', productData);
        productShop.updateAddToCart(productData.productQuantity, $productPanel);
        productShop.updateOutOfStockTitle(productData.productQuantity.availability, $productPanel);
        this.renderPowerReviews(reviewProductId, { ReviewSnippet: 'pr-reviewsnippet-quick-buy' });
    },

    /**
     * Fills option templates with isolated radio groups and labels.
     * @param {Array} optionGroups - Product variations.
     */
    fillOptions: function (optionGroups) {
        const $optionsContainer = this.$quickBuyModal.find('.product-main-options');
        const groupTemplate = this.$quickBuyModal.find('.quick-buy__option-group-template')[0];
        const optionTemplate = this.$quickBuyModal.find('.quick-buy__option-template')[0];

        $optionsContainer.empty().toggle(optionGroups.length > 0);
        optionGroups.forEach((optionGroup, groupIndex) => {
            const $optionGroup = $(document.importNode(groupTemplate.content.firstElementChild, true));
            const $radioGroup = $optionGroup.find('.form-group--product-radio-group');
            const radioGroupName = 'quick-buy-option-' + groupIndex;

            $optionGroup.toggle(optionGroup.values.length > 1);
            if (optionGroup.valueType === 'Color') {
                $optionGroup.find('.product-main-options__title').remove();
            } else {
                $optionGroup.find('.product-main-options__title span').text(optionGroup.label);
            }
            $radioGroup.attr('data-option-name', optionGroup.label)
                .addClass(optionGroup.valueType === 'Color' ? 'form-group--color-picker' : 'form-group--chips');

            optionGroup.values.forEach((optionValue, optionIndex) => {
                const $option = $(document.importNode(optionTemplate.content.firstElementChild, true));
                const optionInputId = radioGroupName + '-' + optionIndex;

                $option.find('input').attr({ id: optionInputId, name: radioGroupName, value: optionValue.value })
                    .prop('checked', !!optionValue.selected)
                    .prop('disabled', !!optionValue.disabled);
                $option.find('label').attr('for', optionInputId).text(optionValue.value);
                if (optionGroup.valueType === 'Color') {
                    $option.find('input').css('background-color', optionValue.key);
                    $option.find('label').prepend($('<span>').text(optionGroup.label + ' - '));
                }
                $radioGroup.append($option);
            });
            $optionsContainer.append($optionGroup);
        });
    },

    /**
     * Fills metric icons and tooltip text.
     * @param {Array} metrics - Product metrics.
     */
    fillMetrics: function (metrics) {
        const $metricsContainer = this.$quickBuyModal.find('.product-metrics');
        const metricTemplate = this.$quickBuyModal.find('.quick-buy__metric-template')[0];
        const validMetrics = Array.isArray(metrics)
            ? metrics.filter(metric => metric && typeof metric === 'object' && !Array.isArray(metric)) : [];

        $metricsContainer.empty().hide();
        if (!metricTemplate?.content?.firstElementChild) {
            return;
        }

        validMetrics.forEach((metric, metricIndex) => {
            const iconMarkup = typeof metric.icon === 'string' ? metric.icon.trim() : '';
            const labelName = typeof metric.labelName === 'string' ? metric.labelName : '';
            const labelDescription = typeof metric.labelDescription === 'string' ? metric.labelDescription : '';
            const tooltipTitle = typeof metric.tooltip?.title === 'string' ? metric.tooltip.title : '';
            const tooltipDescription = typeof metric.tooltip?.description === 'string'
                ? metric.tooltip.description : '';
            if (!iconMarkup && !labelName && !labelDescription && !tooltipTitle && !tooltipDescription) {
                return;
            }

            const $metric = $(document.importNode(metricTemplate.content.firstElementChild, true));
            if (typeof metric.cssClass === 'string' && metric.cssClass) {
                const metricClasses = metric.cssClass.trim().split(/\s+/)
                    .filter(className => className && className !== 'product-metric')
                    .map(className => className.startsWith('product-metric--')
                        ? className : 'product-metric--' + className);
                $metric.addClass(metricClasses.join(' '));
            }
            $metric.find('.product-metric__indicator').html(iconMarkup).toggle(!!iconMarkup);
            $metric.find('.product-metric__name').text(labelName);
            $metric.find('.product-metric__name-description').text(labelDescription);
            const tooltipId = 'quick-buy-metric-tooltip-' + metricIndex;
            $metric.find('.tooltip-icon-btn').attr('aria-describedby', tooltipId);
            $metric.find('.tooltip__content').attr('id', tooltipId);
            $metric.find('.tooltip__heading').text(tooltipTitle).toggle(!!tooltipTitle);
            $metric.find('.tooltip__text p').text(tooltipDescription);
            $metric.find('.tooltip__text').toggle(!!tooltipDescription);
            $metric.find('.product-metric__info').toggle(!!(tooltipTitle || tooltipDescription));
            $metricsContainer.append($metric);
            $metricsContainer.show();
        });
    },

    /** Clears product values and widgets while retaining the modal template. */
    clearProduct: function () {
        const $productPanel = this.$quickBuyModal.find('.product-main');
        const $quantityCounter = $productPanel.find('.counter');

        ++this.latestRequestId;
        this.loadingProductId = null;
        $productPanel.removeData('quickBuyRequestId');
        $productPanel.find('.swiper').each(function () {
            this.swiper?.destroy(true, true);
        });
        if ($quantityCounter.counter('instance')) {
            $quantityCounter.counter('destroy');
        }
        $quantityCounter.removeAttr('data-max');
        $productPanel.find('[data-quick-buy-field]').empty();
        $productPanel.find('.product-cart-add__button').off('click.productAddToCart');
        $productPanel.find('.cta-panel__quick-buy-modal--view-details a').removeAttr('href');
        $productPanel.addClass('hidden').removeData('productSku')
            .removeAttr('data-productid data-ean data-product-url data-initial-sku');
        const productShop = femcare.product.panel.info.shop;
        if (productShop.$activePanel?.[0] === $productPanel[0]) {
            productShop.$activePanel = productShop.$mainPanel;
            productShop.$counter = productShop.$mainPanel.find('.counter');
            productShop.$productMedia = productShop.$mainPanel.find('.product-main__media-panel');
            productShop.ean = productShop.$mainPanel.attr('data-ean');
            productShop.url = productShop.$mainPanel.attr('data-product-url');
            productShop.sku = productShop.$mainPanel.data('productSku');
            productShop.resetSelectedOptions();
            productShop.initSkuOptions(productShop.$mainPanel);
        }
        this.$quickBuyModal.find('.quick-buy__content').attr('aria-busy', 'false');
        this.$quickBuyModal.find('.quick-buy__error').addClass('hidden');
        femcare.loader.toggleLoader(this.$quickBuyModal.find('.modal__body')[0], false);
        femcare.loader.toggleLoader($productPanel.find('.product-main__media-panel')[0], false);
    },

    /** Binds loading and retry clicks; femcare.modal handles the dialog. */
    bindEvents: function () {
        const quickBuyButtonSelector = '.cta-panel .cta-panel__quick-buy-button';

        // Delegation keeps Quick Buy working when product cards are sorted or replaced by search.
        $(document)
            .off('click.quickBuy', quickBuyButtonSelector)
            .on('click.quickBuy', quickBuyButtonSelector, (event) => {
                event.preventDefault();

                const productId = event.currentTarget.dataset.productid;
                const productCard = event.currentTarget.closest('.cta-panel');
                this.loadProduct(productId, {
                    ean: event.currentTarget.dataset.ean || productCard.dataset.ean,
                    url: productCard.dataset.productUrl || event.currentTarget.dataset.productUrl,
                    variationAttributes: femcare.ctaPanel.colorSelector.getVariationAttributes(productCard)
                });
            });

        this.$quickBuyModal.off('modal:closed.quickBuy').on('modal:closed.quickBuy', () => {
            this.clearProduct();
            this.activeProductId = null;
            this.activeProductRequestData = null;
        });

        this.$quickBuyModal.find('.quick-buy__retry').off('click.quickBuy').on('click.quickBuy', () => {
            this.loadProduct(this.activeProductId, this.activeProductRequestData);
        });
    },

    /**
     * Renders reviews using the product's existing configuration.
     * @param {string} productId - Product ID.
     * @param {Object} productReviewComponent - Review component targets.
     */
    renderPowerReviews: function (productId, productReviewComponent) {
        const isPowerReviewsLoaded = window.POWERREVIEWS;
        if (isPowerReviewsLoaded) {
            const productReviewConfigurations = window.pr_render_options || [];
            productReviewConfigurations.forEach(function (reviewConfiguration) {
                const reviewProductId = reviewConfiguration.page_id;

                if (reviewProductId === productId) {
                    const quickBuyReviewConfiguration = $.extend(true, {}, reviewConfiguration);
                    quickBuyReviewConfiguration.components = productReviewComponent;
                    window.POWERREVIEWS.display.render(quickBuyReviewConfiguration);
                }
            });
        }
    }
};
